/**
 * Translate the Assistant's allowlisted navigation contract into app state.
 *
 * The model never supplies a URL, selector, or command. It chooses one exact
 * backend-validated target; the SPA owns this map and therefore owns what the
 * action is allowed to do.
 */

import type {
  AssistantNavigation,
  AssistantNavigationTarget,
} from '../../api/types.ts';
import type { ModelTab, Section, Tab } from '../../state/store.ts';
import type { PageId } from '../../state/page-visibility.ts';
import type { AssistantTab } from '../../state/assistant-surface.ts';

export const ASSISTANT_NAVIGATE_EVENT = 'assistant-navigate';

export interface AssistantNavigationPlan {
  label: string;
  section?: Section;
  tab?: Tab;
  modelTab?: ModelTab;
  assistantTab?: AssistantTab;
  page?: PageId;
  popup?: 'preferences';
  historical?: boolean;
}

const NAVIGATION_PLANS: Record<AssistantNavigationTarget, AssistantNavigationPlan> = {
  home: { label: 'Home', section: 'home' },
  explore_plot: { label: 'Plot', section: 'explore', tab: 'plot', page: 'plot' },
  explore_export: {
    label: 'Export',
    section: 'explore',
    tab: 'export',
    page: 'export',
    historical: true,
  },
  model_viewer_service_power: {
    label: 'Service Power',
    section: 'explore',
    tab: 'modelviewer',
    modelTab: 'sp',
    page: 'modelviewer',
    historical: true,
  },
  model_viewer_sfoc: {
    label: 'SFOC',
    section: 'explore',
    tab: 'modelviewer',
    modelTab: 'sfoc',
    page: 'modelviewer',
    historical: true,
  },
  dashboards: {
    label: 'Dashboards',
    section: 'dashboards',
    page: 'dashboards',
  },
  catalog: { label: 'Catalog', section: 'catalog' },
  catalog_chart: { label: 'Catalog chart', section: 'catalog' },
  assistant_chat: {
    label: 'SONAR chat',
    section: 'assistant',
    assistantTab: 'chat',
    page: 'assistant',
  },
  assistant_history: {
    label: 'SONAR history',
    section: 'assistant',
    assistantTab: 'history',
    page: 'assistant',
  },
  preferences: { label: 'Preferences', popup: 'preferences' },
};

/** Runtime lookup stays defensive even though the generated API type is closed. */
export function planAssistantNavigation(
  action: AssistantNavigation,
): AssistantNavigationPlan | null {
  if (!Object.prototype.hasOwnProperty.call(NAVIGATION_PLANS, action.target)) return null;
  return NAVIGATION_PLANS[action.target];
}

/** Bubble through the light-DOM Assistant surfaces to the application shell. */
export function dispatchAssistantNavigation(
  host: HTMLElement,
  action: AssistantNavigation,
): void {
  host.dispatchEvent(
    new CustomEvent<AssistantNavigation>(ASSISTANT_NAVIGATE_EVENT, {
      detail: action,
      bubbles: true,
      composed: true,
    }),
  );
}
